import * as XLSX from "xlsx";
import i18n from "../../../i18n";

const normalizeText = (value) =>
    String(value || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

const toNumber = (value) => {
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    if (value === null || value === undefined) {
        return null;
    }
    const cleaned = String(value).replace(/\s/g, "").replace(",", ".");
    const parsed = parseFloat(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
};

const toMonthYear = (value) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }

    if (typeof value === "number") {
        const date = XLSX.SSF.parse_date_code(value);
        if (!date) return null;
        return `${String(date.m).padStart(2, "0")}/${date.y}`;
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return `${String(value.getMonth() + 1).padStart(2, "0")}/${value.getFullYear()}`;
    }

    const raw = String(value).trim();
    if (!raw) return null;

    if (/^\d+$/.test(raw)) {
        const serialDate = XLSX.SSF.parse_date_code(parseInt(raw, 10));
        if (serialDate) {
            return `${String(serialDate.m).padStart(2, "0")}/${serialDate.y}`;
        }
    }

    const mmYYYY = raw.match(/^(\d{1,2})[/-](\d{4})$/);
    if (mmYYYY) {
        return `${String(parseInt(mmYYYY[1], 10)).padStart(2, "0")}/${mmYYYY[2]}`;
    }

    const ddMMYYYY = raw.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (ddMMYYYY) {
        return `${String(parseInt(ddMMYYYY[2], 10)).padStart(2, "0")}/${ddMMYYYY[3]}`;
    }

    const parsedDate = new Date(raw);
    if (!Number.isNaN(parsedDate.getTime())) {
        return `${String(parsedDate.getMonth() + 1).padStart(2, "0")}/${parsedDate.getFullYear()}`;
    }

    return null;
};

const findColumnIndex = (headers, aliases) => {
    const normalizedAliases = aliases.map(normalizeText);
    return headers.findIndex((header) => normalizedAliases.includes(normalizeText(header)));
};

const readRowsFromExcel = async (file) => {
    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array", cellDates: true });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
    if (!rows.length) {
        throw new Error(i18n.t('excelImport.emptyFile'));
    }
    return rows;
};

export const parsePrimeEmployeeFile = async (file) => {
    const rows = await readRowsFromExcel(file);
    const headers = rows[0].map((h) => String(h || "").trim());
    const dataRows = rows.slice(1);

    const employeeNameIndex = findColumnIndex(headers, ["nom employe", "nom", "employee", "employee_name", "employe"]);
    const amountIndex = findColumnIndex(headers, ["montant", "amount", "prime"]);
    const matriculeIndex = findColumnIndex(headers, ["matricule", "identifiant", "id"]);

    if ((employeeNameIndex < 0 && matriculeIndex < 0) || amountIndex < 0) {
        throw new Error(i18n.t('excelImport.missingColumns'));
    }

    const items = [];
    for (const row of dataRows) {
        const employeeName = employeeNameIndex >= 0 ? String(row[employeeNameIndex] || "").trim() : "";
        const amount = toNumber(row[amountIndex]);
        const matricule = matriculeIndex >= 0 ? String(row[matriculeIndex] || "").trim() : "";

        if ((!employeeName && !matricule) || amount === null) continue;
        items.push({ employeeName, matricule, amount });
    }

    if (!items.length) {
        throw new Error(i18n.t('excelImport.noValidRows'));
    }

    return items;
};

export const parseDeductionEmployeeFile = async (file) => {
    const rows = await readRowsFromExcel(file);
    const headers = rows[0].map((h) => String(h || "").trim());
    const dataRows = rows.slice(1);

    const employeeNameIndex = findColumnIndex(headers, ["nom employe", "nom", "employee", "employee_name", "employe"]);
    const amountIndex = findColumnIndex(headers, ["montant", "amount"]);
    const matriculeIndex = findColumnIndex(headers, ["matricule", "identifiant", "id"]);
    const startDateIndex = findColumnIndex(headers, ["date debut", "debut", "start_date", "date_start", "debut"]);
    const endDateIndex = findColumnIndex(headers, ["date fin", "fin", "end_date", "date_end"]);

    if ((employeeNameIndex < 0 && matriculeIndex < 0) || amountIndex < 0) {
        throw new Error(i18n.t('excelImport.missingColumns'));
    }

    const items = [];
    for (const row of dataRows) {
        const employeeName = employeeNameIndex >= 0 ? String(row[employeeNameIndex] || "").trim() : "";
        const amount = toNumber(row[amountIndex]);
        const matricule = matriculeIndex >= 0 ? String(row[matriculeIndex] || "").trim() : "";
        const dateStart = startDateIndex >= 0 ? toMonthYear(row[startDateIndex]) : null;
        const dateEnd = endDateIndex >= 0 ? toMonthYear(row[endDateIndex]) : null;

        if ((!employeeName && !matricule) || amount === null) continue;
        items.push({ employeeName, matricule, amount, dateStart, dateEnd });
    }

    if (!items.length) {
        throw new Error(i18n.t('excelImport.noValidRows'));
    }

    return items;
};

export const normalizeEmployeeName = (name) => normalizeText(name);
export const normalizeEmployeeMatricule = (matricule) => normalizeText(matricule);
